import { useEffect, useState } from 'react';
import styled from 'styled-components';

import { MessageMedium } from 'constants/messageMediums';
import { AceEditor, AceEditorTheme } from 'modules/aceEditor';
import { DragAndDropEditor } from 'modules/dragAndDropEditor';
import { EmailContentDefinition, HtmlEditor } from 'modules/graphql/types';
import { SideBySideEditor } from 'modules/sideBySideEditor';
import { ModalType, useModalContext } from 'modules/templateEditor/context';
import { WYSIWYGEditor } from 'modules/wysiwygEditor';
import { InfoBanner } from 'ui/banner';
import { FormField } from 'ui/formField';
import { FullScreenLayout } from 'ui/fullScreen/FullScreenLayout';
import { TextArea } from 'ui/inputs';
import noop from 'utils/noop';

import { CheckboxField, LayoutContentContainerFullWidth } from '../common';
import { ContentEditorNotSelected } from '../common/ContentEditorNotSelected';
import { ContentFooter } from '../common/ContentFooter';
import { ContentHeader, EditSavedRowDesignContentHeader } from '../common/ContentHeader';
import { ContentControlBar } from '../common/Controls/ContentControlBar';
import { EditSavedRowDesignWarning } from '../common/EditSavedRowDesignWarning';
import { SettingsDrawer } from '../common/SettingsDrawer';
import { displayContent } from '../common/content';
import { DragAndDropCallback, EditorView, TemplateEditViews } from '../common/types';
import { useContentEditorPreferencesContext } from '../context/ContentEditorPreferencesContext';
import { useEditSavedRowDesignContext } from '../context/EditSavedRowDesignContext';
import { useEditableFieldsContext } from '../context/EditableFieldsContext';
import { useQueryParamContext } from '../context/QueryParamContext';
import { useContentEditorChangeHandlers } from '../hooks/useContentEditorChangeHandlers';
import { useInitialDragAndDropLoad } from '../hooks/useInitialDragAndDropLoad/useInitialDragAndDropLoad';
import { ContentEditorPreferencesModal } from '../modals/ContentEditorPreferencesModal';
import { SmsSmartOptInModal } from '../modals/SmsSmartOptInModal';
import { useSmsSmartOptInContext } from '../modals/SmsSmartOptInModal/contexts/SmsSmartOptInContext';
import { ContentEditorViewOptions } from './ContentEditorViewDropdown';

export const TEMPLATE_EDITOR_EMAIL_CONTENT = 'template-editor-email-content';
export const ampEditorId = 'template-editor-email-amp';
export const plainTextEditorId = 'template-editor-email-plain-text';

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const HTMLEditorContainer = styled.div<{ isVisible: boolean }>`
  ${({ isVisible }) =>
    isVisible
      ? ``
      : `display: none;`} /** 'display: none;' remains in DOM, but is removed from the layout */
  height: 100%;
`;

const StyledFullScreenLayout = styled(FullScreenLayout)<{ isVisible: boolean }>`
  ${({ isVisible }) =>
    isVisible
      ? ``
      : `display: none;`}/** 'display: none;' remains in DOM, but is removed from the layout */
`;

const StyledLayoutContentContainer = styled(LayoutContentContainerFullWidth)`
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  margin-bottom: ${({ theme }) => theme.spacing.utils.pxToRem(12)};
  margin-top: ${({ theme }) => theme.spacing.utils.pxToRem(25)};
  min-height: ${({ theme }) => theme.spacing.utils.pxToRem(400)};
`;

const StyledFormField = styled(FormField)`
  padding: ${({ theme }) =>
    `${theme.spacing.utils.pxToRem(10)} ${theme.spacing.utils.pxToRem(16)}`};
`;

const StyledTextArea = styled(TextArea)`
  display: flex;
  flex-direction: row;
  flex-grow: 1;
  textarea {
    min-height: 100%;
  }
`;

const AceEditorWrapper = styled.div`
  border-radius: ${({ theme }) => theme.spacing.utils.pxToRem(4)};
  height: 100%;
  margin-top: ${({ theme }) => theme.spacing.utils.pxToRem(10)};
  overflow: hidden;
`;

const PlainEditorContentContainer = styled.div`
  height: inherit;
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  overflow: hidden;
  resize: vertical;
`;

export const ContentCore = () => {
  const {
    contentEditor,
    contentEditorView,
    getDragAndDropContentRef,
    dragAndDropInitTs,
    dragAndDropInitValue,
    initialTemplate,
    liveTemplate,
    isDirty,
  } = useEditableFieldsContext();

  const emailData = (liveTemplate?.mediumData || {}) as EmailContentDefinition;
  const initialEmailData = (initialTemplate?.mediumData || {}) as EmailContentDefinition;
  const locale = emailData?.locale?.name || ''; // important to use the locale from the mediumData so we know that the localized beeBuilderMetadata is also up to date
  const lastSavedHTML = initialEmailData.html;
  const valueAMP = emailData.ampHtml;
  const valueHTML = emailData.html;
  const valueText = emailData.plainText;

  const { onHtmlChangeRef, onJsonChangeRef, onAmpChangeRef, onTextChangeRef } =
    useContentEditorChangeHandlers();
  const editSavedRowDesign = useEditSavedRowDesignContext();

  const initialDragAndDropLoad = useInitialDragAndDropLoad<EmailContentDefinition>();

  const [useCustomPlainText, setUseCustomPlainText] = useState<boolean>(!!valueText);
  const { openModal } = useModalContext();

  const { isModalOpen: isPreferencesModalOpen, selectedSideBySideTheme } =
    useContentEditorPreferencesContext();
  const { isModalOpen: isSmsSmartOpenInModalOpen } = useSmsSmartOptInContext();
  const { view } = useQueryParamContext();
  const [shouldInitEditor, setShouldInitEditor] = useState<boolean>(false);

  useEffect(() => {
    // The Content component is always rendered (to prevent reloading of the editor when switching
    // views) so we only want to make decisions regarding editor selection when we actually try to
    // view the content
    if (!contentEditor && view === TemplateEditViews.content) {
      openModal(ModalType.SelectContentEditorModals);
    }
  }, [contentEditor, view, openModal]);

  useEffect(() => {
    // Since this component is always rendered, we need an indicator to determine when we first view
    // it in order to trigger the actual initialization of the editors (once the Wysiwyg editor is
    // rendered/mounted, it will auto-correct the valueHTML if needed which can trigger a change)
    if (!shouldInitEditor && view === TemplateEditViews.content) {
      setShouldInitEditor(true);
    }
  }, [shouldInitEditor, view]);

  const renderEditor = () => {
    if (contentEditor === HtmlEditor.Bee && !!emailData.beeBuilderMetadata) {
      return (
        shouldInitEditor && (
          <DragAndDropEditor
            contentLocale={locale}
            initTs={dragAndDropInitTs}
            initValue={dragAndDropInitValue}
            onChange={onJsonChangeRef.current}
            onGetContentRef={getDragAndDropContentRef}
            messageMedium={MessageMedium.Email}
            onUpdateValues={(json: string, html: string, updateCallback: DragAndDropCallback) => {
              onHtmlChangeRef.current(html);
              // onJsonChangeRef.current(json); // ignore this json (it has added properties)
              updateCallback({ html });
            }}
            onEditorLoad={initialDragAndDropLoad.current}
            editSavedRowDesign={editSavedRowDesign}
            hasUnsavedTemplateChanges={isDirty}
          />
        )
      );
    }
    if (contentEditor === HtmlEditor.Wysiwyg) {
      return (
        shouldInitEditor && (
          <WYSIWYGEditor
            canLoadHTML
            canUseAutoCompleteParams
            contentLocale={locale}
            height="100%"
            isFullHtmlDoc
            lastSavedValue={lastSavedHTML}
            onChange={onHtmlChangeRef.current}
            value={valueHTML}
          />
        )
      );
    }
    if (contentEditor === HtmlEditor.Ace) {
      // Since the side-by-side editor performs some measurements upon mount, it is important that
      // the component is available in the layout so that it can be measured at that time. The
      // following rendering workaround is necessary because the Content component is always
      // rendered to solve rendering speed (drag-and-drop) and undo issues (wysiwyg) of the other
      // editors. To handle visibility in the UI, containers of all of the editors are added/removed
      // from the layout with the css display property, so it is important to only render the
      // side-by-side editor when it is also intended to be visible in the layout (so that it can be
      // measured) - otherwise it won't be initially visible.
      // note: it is not necessary to also check contentEditorView because HTML will always render
      // first providing the initial measurement
      return (
        shouldInitEditor &&
        view === TemplateEditViews.content && (
          <SideBySideEditor
            canLoadHTML
            height="100%"
            isFullHtmlDoc
            name="content-editor-ace"
            onChange={onHtmlChangeRef.current}
            theme={selectedSideBySideTheme}
            value={valueHTML}
          />
        )
      );
    }
    return <ContentEditorNotSelected />;
  };

  // The HTML Editor component is always rendered to prevent reloading of the editor when switching
  // contentEditorView (this preserves undo/redo states in WYSIWYG and provides a smoother
  // experience in Drag-and-drop)
  return (
    <>
      <HTMLEditorContainer isVisible={contentEditorView === EditorView.HTML}>
        {renderEditor()}
      </HTMLEditorContainer>
      {contentEditorView === EditorView.TEXT && (
        <PlainEditorContentContainer>
          <InfoBanner>{displayContent.strEmailContentTextOptionInfo}</InfoBanner>
          <StyledFormField>
            <CheckboxField
              label={displayContent.labelEmailContentTextOption}
              status={useCustomPlainText}
              onClick={() => {
                if (useCustomPlainText) {
                  onTextChangeRef.current('');
                }
                setUseCustomPlainText(!useCustomPlainText);
              }}
            />
          </StyledFormField>
          <StyledTextArea
            id={plainTextEditorId}
            isDisabled={!useCustomPlainText}
            onChange={onTextChangeRef.current}
            value={valueText}
          />
        </PlainEditorContentContainer>
      )}
      {contentEditorView === EditorView.AMP && (
        <>
          <InfoBanner>
            <p>
              Requires registration with Gmail. See:{' '}
              <a
                target="_blank"
                rel="noopener noreferrer"
                href="https://developers.google.com/gmail/ampemail/"
              >
                https://developers.google.com/gmail/ampemail/
              </a>
            </p>
            <p>
              Use double pipes for handlebars parameters, e.g. ||email||. Curly braces are reserved
              for amp-mustache. See:{' '}
              <a
                target="_blank"
                rel="noopener noreferrer"
                href="https://amp.dev/documentation/components/amp-mustache/"
              >
                https://amp.dev/documentation/components/amp-mustache/
              </a>
            </p>
          </InfoBanner>
          <AceEditorWrapper>
            <AceEditor
              editorProps={{
                enableBasicAutocompletion: true,
                enableLiveAutocompletion: false,
                enableSnippets: true,
                $blockScrolling: Infinity,
              }}
              fontSize={14}
              height="100%"
              mode="handlebars"
              name={ampEditorId}
              onBlur={noop}
              onChange={onAmpChangeRef.current}
              onFocus={noop}
              onInput={noop}
              onPaste={noop}
              placeholder=""
              showPrintMargin={false}
              theme={AceEditorTheme.Monokai}
              value={valueAMP}
              width="100%"
              wrapEnabled={true}
            />
          </AceEditorWrapper>
        </>
      )}
      {isPreferencesModalOpen && <ContentEditorPreferencesModal />}
      {isSmsSmartOpenInModalOpen && <SmsSmartOptInModal />}
    </>
  );
};

type Props = {
  isVisible: boolean;
};

export const Content = ({ isVisible }: Props) => {
  const { isEditingSavedRowDesign } = useEditSavedRowDesignContext();
  const { contentEditor, contentEditorView } = useEditableFieldsContext();

  return (
    <StyledFullScreenLayout
      actionBarIsAnchored
      DrawerContent={<SettingsDrawer />}
      HeaderContent={
        isEditingSavedRowDesign ? <EditSavedRowDesignContentHeader /> : <ContentHeader />
      }
      FooterContent={
        <ContentFooter
          showAssetBtn={contentEditor === HtmlEditor.Bee && contentEditorView === EditorView.HTML}
          showPreferencesBtn={!!contentEditor}
          showSnippetBtn={!!contentEditor && contentEditorView !== EditorView.AMP}
          showDetailsBtn={!isEditingSavedRowDesign}
        />
      }
      isVisible={isVisible}
    >
      <ContentContainer data-test={TEMPLATE_EDITOR_EMAIL_CONTENT}>
        {isEditingSavedRowDesign ? (
          <EditSavedRowDesignWarning />
        ) : (
          <ContentControlBar>
            <ContentEditorViewOptions />
          </ContentControlBar>
        )}
        <StyledLayoutContentContainer>
          <ContentCore />
        </StyledLayoutContentContainer>
      </ContentContainer>
    </StyledFullScreenLayout>
  );
};

export default Content;
